import { api } from '../api.js';
import {
  h, money, moneyCents, section, pair, targetBar, paceChart, monthBars, monthName, shortDate,
} from '../ui.js';

// Budget: spending over time against one monthly target. This month day by day
// (against last month and the target's pace), the last year by month, and where
// this month's money went. No categories: payees are the breakdown.

interface Budget {
  today: string;
  target: number | null;
  months: { month: string; total: number }[];
  average: number;
  this_month: { month: string; days: number; cumulative: number[] };
  last_month: { month: string; cumulative: number[] };
  payees: { description: string; count: number; total: number; last_month: number }[];
  big_purchases: { id: number; date: string; description: string; amount: number; account: string }[];
}

export function budgetScreen(): HTMLElement {
  const root = h('div', 'screen');
  const top = h('div', 'topbar');
  const body = h('div');
  body.appendChild(h('div', 'loading', 'Loading…'));
  top.appendChild(h('div', 'title', 'Budget'));
  root.append(top, body);

  async function load(): Promise<void> {
    try {
      render(await api.get<Budget>('/budget'));
    } catch (err) {
      if (err instanceof Error && err.message === 'Unauthorized') return;
      body.innerHTML = '';
      body.appendChild(h('div', 'error', err instanceof Error ? err.message : 'Could not load'));
    }
  }

  async function editTarget(current: number | null): Promise<void> {
    const raw = prompt('Monthly spending target (empty to remove)', current ? String(current) : '6000');
    if (raw == null) return;
    const n = parseFloat(raw.replace(/[$,\s]/g, ''));
    await api.patch('/settings/spend_target', { value: raw.trim() && isFinite(n) && n > 0 ? String(Math.round(n)) : '' });
    void load();
  }

  function render(d: Budget): void {
    body.innerHTML = '';
    const tm = d.this_month;
    const spent = tm.cumulative[tm.cumulative.length - 1] ?? 0;
    const day = tm.cumulative.length;
    const lastSame = d.last_month.cumulative[Math.min(day, d.last_month.cumulative.length) - 1] ?? 0;

    // ── This month ──
    const now = section(monthName(tm.month), { label: d.target ? 'Target' : 'Set target', onClick: () => void editTarget(d.target) });
    now.body.append(
      h('div', 'label', 'Spent so far'),
      h('div', 'money hero', money(spent)),
    );
    if (d.target) now.body.appendChild(targetBar(spent, d.target, day, tm.days));
    else now.body.appendChild(h('div', 'note', 'Set one monthly target to see your pace against it.'));
    now.body.appendChild(paceChart(tm.cumulative, d.last_month.cumulative, tm.days, d.target));
    const legend = h('div', 'legend');
    legend.append(h('span', 'key this', 'This month'), h('span', 'key last', monthName(d.last_month.month).split(' ')[0]));
    if (d.target) legend.appendChild(h('span', 'key pace', 'Target pace'));
    now.body.appendChild(legend);
    now.body.appendChild(pair([
      { label: `By ${shortDate(`${d.last_month.month}-${String(Math.min(day, d.last_month.cumulative.length)).padStart(2, '0')}`)}`, value: money(lastSame) },
      { label: 'Difference', value: `${spent >= lastSame ? '+' : '−'}${money(Math.abs(spent - lastSame))}`, cls: spent > lastSame ? 'down' : 'up' },
    ]));
    body.appendChild(now.el);

    // ── By month ──
    const year = section('By month');
    const caption = h('div', 'note');
    const describe = (m: { month: string; total: number }) => {
      const vs = d.target ? ` · ${m.total > d.target ? `${money(m.total - d.target)} over` : `${money(d.target - m.total)} under`} target` : '';
      caption.textContent = `${monthName(m.month)}: ${money(m.total)}${vs}`;
    };
    year.body.append(
      monthBars(d.months, d.target, describe),
      caption,
    );
    caption.textContent = `Average ${money(d.average)} a month over the last ${Math.min(6, d.months.length - 1)} full months. Tap a bar for that month.`;
    body.appendChild(year.el);

    // ── Where it went ──
    if (d.payees.length) {
      const where = section('Where it went this month');
      for (const p of d.payees) {
        const row = h('div', 'row');
        const main = h('div', 'row-main');
        main.append(h('div', '', p.description), h('div', 'row-sub',
          `${p.count} ${p.count === 1 ? 'charge' : 'charges'}${p.last_month ? ` · ${money(p.last_month)} last month` : ' · new this month'}`));
        row.append(main, h('div', 'money', money(p.total)));
        where.body.appendChild(row);
      }
      body.appendChild(where.el);
    }

    if (d.big_purchases.length) {
      const big = section('Big purchases');
      for (const b of d.big_purchases) {
        const row = h('div', 'txn');
        const desc = h('div', 'txn-desc');
        desc.append(h('span', '', b.description), h('span', 'tag', b.account.split(' ')[0]));
        row.append(h('div', 'txn-date', shortDate(b.date)), desc, h('div', 'txn-amt', moneyCents(b.amount)));
        big.body.appendChild(row);
      }
      body.appendChild(big.el);
    }
  }

  void load();
  return root;
}
